import React, { useMemo, useState } from 'react';

interface AudienceStudent {
  id: number;
  name: string;
  group_name?: string;
  joined: boolean;
  has_entered_live?: boolean;
  participation_status?: 'absent' | 'present_no_work' | 'participated';
  effective_attendance_status?: 'present' | 'absent' | null;
}

interface OpsAudiencePanelProps {
  joinedStudentIds: number[];
  enteredLiveStudentIds: number[];
  livePresenceSummary?: {
    absent: number;
    present_no_work: number;
    participated: number;
  } | null;
  students: AudienceStudent[];
  selectedStudentIds: number[];
  onToggleStudentSelection: (studentId: number) => void;
  onSelectStudentIds: (studentIds: number[]) => void;
  onPullSelected: () => void;
  onReleaseSelected: () => void;
  onPullStudentIds: (studentIds: number[]) => void;
  onReleaseStudentIds: (studentIds: number[]) => void;
  onReleaseAll: () => void;
  attendanceSavingStudentIds: number[];
  attendanceError?: string | null;
  onSetAttendance: (studentId: number, status: 'present' | 'absent') => void;
}

export const OpsAudiencePanel: React.FC<OpsAudiencePanelProps> = ({
  joinedStudentIds,
  enteredLiveStudentIds,
  livePresenceSummary,
  students,
  selectedStudentIds,
  onToggleStudentSelection,
  onSelectStudentIds,
  onPullSelected,
  onReleaseSelected,
  onPullStudentIds,
  onReleaseStudentIds,
  onReleaseAll,
  attendanceSavingStudentIds,
  attendanceError,
  onSetAttendance,
}) => {
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const subgroupOptions = useMemo(() => (
    Array.from(
      new Set(
        students
          .map((student) => String(student.group_name || '').trim())
          .filter((groupName) => groupName.length > 0),
      ),
    ).sort((left, right) => left.localeCompare(right))
  ), [students]);
  const selectedGroupStudentIds = useMemo(() => (
    students
      .filter((student) => String(student.group_name || '').trim() === selectedGroupName)
      .map((student) => student.id)
  ), [selectedGroupName, students]);

  return (
    <>
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs uppercase tracking-wide text-gray-400">Audience</p>
      <div className="text-right">
        <div className="text-xs text-gray-500">
          {joinedStudentIds.length} joined
        </div>
        <div className="text-xs text-gray-500">
          {enteredLiveStudentIds.length} entered live
        </div>
      </div>
    </div>
    {livePresenceSummary && (
      <div className="mb-3 flex flex-wrap gap-2 text-[11px]">
        <span className="px-2 py-1 rounded bg-slate-100 text-slate-700">
          Absent: {livePresenceSummary.absent}
        </span>
        <span className="px-2 py-1 rounded bg-amber-100 text-amber-700">
          Present no work: {livePresenceSummary.present_no_work}
        </span>
        <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700">
          Participated: {livePresenceSummary.participated}
        </span>
      </div>
    )}
    {attendanceError ? (
      <div className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
        {attendanceError}
      </div>
    ) : null}

    <div className="space-y-2 max-h-64 overflow-y-auto">
      {students.map((student) => (
        <div
          key={student.id}
          className="flex items-center gap-2 p-2 rounded border border-gray-200"
        >
          <input
            type="checkbox"
            checked={selectedStudentIds.includes(student.id)}
            aria-label={`Select ${student.name} for instruction`}
            onChange={() => onToggleStudentSelection(student.id)}
            className="rounded"
          />
          <span className="text-sm flex-1">{student.name}</span>
          {student.group_name && (
            <span className="px-2 py-1 text-xs bg-violet-100 text-violet-700 rounded">
              {student.group_name}
            </span>
          )}
          {student.joined && (
            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Joined</span>
          )}
          {student.has_entered_live ? (
            <span className="px-2 py-1 text-xs bg-sky-100 text-sky-700 rounded">Entered</span>
          ) : (
            <span className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded">Not entered</span>
          )}
          {student.participation_status === 'participated' && (
            <span className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded">Participated</span>
          )}
          {student.participation_status === 'present_no_work' && (
            <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded">No work yet</span>
          )}
          {student.effective_attendance_status === 'absent' ? (
            <button
              type="button"
              aria-label={`Mark ${student.name} present`}
              className="rounded bg-rose-100 px-2 py-1 text-xs font-medium text-rose-700 disabled:opacity-50"
              onClick={() => onSetAttendance(student.id, 'present')}
              disabled={attendanceSavingStudentIds.includes(student.id)}
            >
              {attendanceSavingStudentIds.includes(student.id) ? 'Saving…' : 'Absent · mark present'}
            </button>
          ) : (
            <button
              type="button"
              aria-label={`Mark ${student.name} absent`}
              className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => onSetAttendance(student.id, 'absent')}
              disabled={attendanceSavingStudentIds.includes(student.id)}
            >
              {attendanceSavingStudentIds.includes(student.id) ? 'Saving…' : 'Mark absent'}
            </button>
          )}
        </div>
      ))}
    </div>

    <div className="mt-3 space-y-2">
      {subgroupOptions.length > 0 && (
        <div className="rounded border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex-1 min-w-[180px]">
              <span className="block text-[11px] font-medium uppercase tracking-wide text-gray-500">
                Saved subgroup
              </span>
              <select
                className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900"
                value={selectedGroupName}
                onChange={(event) => setSelectedGroupName(event.target.value)}
              >
                <option value="">Choose subgroup</option>
                {subgroupOptions.map((groupName) => (
                  <option key={groupName} value={groupName}>
                    {groupName}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
              onClick={() => onSelectStudentIds(selectedGroupStudentIds)}
              disabled={selectedGroupStudentIds.length === 0}
            >
              Select subgroup ({selectedGroupStudentIds.length})
            </button>
          </div>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            <button
              type="button"
              className="w-full px-3 py-2 text-sm bg-indigo-100 text-indigo-800 rounded disabled:opacity-50"
              onClick={() => onPullStudentIds(selectedGroupStudentIds)}
              disabled={selectedGroupStudentIds.length === 0}
            >
              Pull subgroup ({selectedGroupStudentIds.length})
            </button>
            <button
              type="button"
              className="w-full px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded disabled:opacity-50"
              onClick={() => onReleaseStudentIds(selectedGroupStudentIds)}
              disabled={selectedGroupStudentIds.length === 0}
            >
              Release subgroup ({selectedGroupStudentIds.length})
            </button>
          </div>
        </div>
      )}
      <button
        className="w-full px-3 py-2 text-sm bg-indigo-600 text-white rounded disabled:opacity-50"
        onClick={onPullSelected}
        disabled={selectedStudentIds.length === 0}
      >
        Pull Selected ({selectedStudentIds.length})
      </button>
      <button
        className="w-full px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded disabled:opacity-50"
        onClick={onReleaseSelected}
        disabled={selectedStudentIds.length === 0}
      >
        Release Selected ({selectedStudentIds.length})
      </button>
      <button
        className="w-full px-3 py-2 text-sm bg-red-100 text-red-700 rounded disabled:opacity-50"
        onClick={onReleaseAll}
        disabled={joinedStudentIds.length === 0}
      >
        Release All ({joinedStudentIds.length})
      </button>
    </div>
  </>
  );
};
